import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels, useFlags, useRequestData, usePrefs } from 'wsm-common-data';
import { useWindowDimension } from 'wsm-srp-utilities';
import { useSelector, useDispatch } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import { setOffSiteData } from '../actions/offSiteData';
import VehicleCard from './VehicleCard';
import VehicleCardSkeleton from './VehicleCardSkeleton';
import ErrorMessage from './ErrorMessage';
import { getShouldShowCompare } from '../utilities/compare';
import { getIsSmallScreen } from '../utilities/layout';

const VehicleCards = ({
	enableMediaCarousel,
	inventoryObj,
	isLoading,
	isMyCarsSitePropEnabled,
	isRequesting,
	isTransition,
	loadingDelayed
}) => {
	const { layout } = useSelector(state => state.layout);
	const labels = useLabels();
	const flags = useFlags();
	const { enableCompare } = usePrefs();
	const { deviceType } = useRequestData();
	const { viewportWidth } = useWindowDimension();
	const isSmallScreen = getIsSmallScreen(viewportWidth);
	// "Grid View" only kicks in above tablet size.
	// A list-type horizontal card still shows for > 480 && < 768
	const isGridLayout = layout === 'grid' && !isSmallScreen;
	const isTabbedLayout = isSmallScreen || isGridLayout;

	const shouldShowCompare = getShouldShowCompare(
		viewportWidth,
		enableCompare,
		deviceType
	);

	const onDemandLoad =
		flags['srp-on-demand-card-loading'] ||
		(isBrowser &&
			window.DDC &&
			window.DDC.getUrlParams &&
			!!window.DDC.getUrlParams()._toggleDelayLoadSRP);
	const dispatch = useDispatch();

	const [offSiteDataState, setOffSiteDataState] = useState({
		offSiteBannerPosition: -1,
		offSiteItemCount: -1
	});
	const [cards, setCards] = useState([]);
	const canUseIntersectionObserver =
		typeof IntersectionObserver !== 'undefined';
	const [cardsViewed, setCardsViewed] = useState([]);
	const cardsViewedRef = useRef(cardsViewed);
	const { offSiteData } = window.DDC.InvData.getFacets() || undefined;

	// TODO remove this observer when srp-on-demand-card-loading FF is removed
	const [vehicleImageObs, setVehicleImageObs] = useState();
	const handleImageIntersect = (entries, obs) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				obs.unobserve(entry.target);
				entry.target.classList.add('loaded');
				if (entry.target.dataset && entry.target.dataset.src) {
					entry.target.src = entry.target.dataset.src; // eslint-disable-line no-param-reassign
				}
			}
		});
	};
	if (canUseIntersectionObserver && !onDemandLoad && !vehicleImageObs) {
		setVehicleImageObs(new IntersectionObserver(handleImageIntersect));
	}

	const dealershipName =
		(isBrowser &&
			window.DDC &&
			window.DDC.dataLayer &&
			window.DDC.dataLayer.dealership &&
			window.DDC.dataLayer.dealership.dealershipName) ||
		'';
	let enableOffSiteToggle = false;
	enableOffSiteToggle = offSiteData && offSiteData.offSiteItemCount !== -1;

	useEffect(() => {
		if (enableOffSiteToggle) {
			setOffSiteDataState({ ...offSiteData });
			dispatch(setOffSiteData(offSiteData));
		}
	}, [offSiteData, enableOffSiteToggle, dispatch]);
	const [showOffSite, setShowOffSite] = useState(
		(sessionStorage.getItem('showOffSite') && true) || false
	);
	let offSiteBannerPosition;
	if (enableOffSiteToggle) {
		// eslint-disable-next-line prefer-destructuring
		offSiteBannerPosition =
			offSiteDataState.offSiteBannerPosition === -1
				? 0
				: offSiteDataState.offSiteBannerPosition;
	}

	const observerRef = useRef(null);

	const observerCallback = useCallback(
		entries => {
			const newCardsViewed = cardsViewedRef.current;
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					// Only dispatch the event if the WIAPI is available.
					if (isBrowser) {
						window.dispatchEvent(
							new CustomEvent('wsInvListingShown', {
								detail: {
									uuid: entry.target.dataset.uuid
								}
							})
						);
					}
					observerRef.current.unobserve(entry.target);
					if (
						!cardsViewedRef.current.includes(
							entry.target.dataset.uuid
						)
					) {
						newCardsViewed.push(entry.target.dataset.uuid);
					}
				}
			});
			setCardsViewed([...newCardsViewed]);
		},
		[cardsViewedRef, observerRef]
	);

	useEffect(() => {
		const observerOptions = {
			rootMargin: '100px'
		};
		if (
			canUseIntersectionObserver &&
			onDemandLoad &&
			!observerRef.current
		) {
			observerRef.current = new IntersectionObserver(
				observerCallback,
				observerOptions
			);
			cardsViewedRef.current = cardsViewed;

			return observerRef.current
				? () => observerRef.current.disconnect()
				: () => {};
		}
		return () => {};
	}, [
		cardsViewed,
		observerRef,
		observerCallback,
		onDemandLoad,
		canUseIntersectionObserver
	]);

	const { incentives, accounts, inventory, pageInfo } = inventoryObj;
	useEffect(() => {
		if (isLoading) {
			setCards(
				inventory.map(_i => (
					<VehicleCardSkeleton
						key={_i.uuid}
						{...{ isGridLayout, isTabbedLayout }}
					/>
				))
			);
		} else {
			const vehicleCardDefaultProps = {
				accounts,
				incentives,
				pageInfo,
				shouldShowCompare,
				enableMediaCarousel,
				isGridLayout,
				isMyCarsSitePropEnabled,
				isTabbedLayout,
				observerRef,
				vehicleImageObs,
				loadingDelayed
			};
			setCards(
				inventory.map((vehicle, vehicleIndex) => {
					const doFullRender =
						!onDemandLoad ||
						canUseIntersectionObserver === false ||
						cardsViewed.includes(vehicle.uuid) ||
						(vehicleIndex === 0 && !isGridLayout) ||
						(vehicleIndex < 3 && isGridLayout);
					const vehicleCardProps = {
						...vehicleCardDefaultProps,
						vehicle,
						doFullRender
					};
					const isFirstOffsiteCard =
						vehicleIndex === offSiteBannerPosition;
					if (
						enableOffSiteToggle &&
						vehicle.offSite &&
						!isFirstOffsiteCard &&
						!showOffSite
					) {
						return null;
					}
					return (
						<VehicleCard
							{...vehicleCardProps}
							key={vehicle.uuid}
							setShowOffSite={
								enableOffSiteToggle &&
								isFirstOffsiteCard &&
								!showOffSite
									? setShowOffSite
									: undefined
							}
						/>
					);
				})
			);
		}
	}, [
		accounts,
		canUseIntersectionObserver,
		cardsViewed,
		shouldShowCompare,
		enableMediaCarousel,
		enableOffSiteToggle,
		incentives,
		inventory,
		isGridLayout,
		isLoading,
		isMyCarsSitePropEnabled,
		isTabbedLayout,
		onDemandLoad,
		vehicleImageObs,
		observerRef,
		offSiteBannerPosition,
		pageInfo,
		showOffSite,
		loadingDelayed
	]);

	return (
		<React.Fragment>
			{enableOffSiteToggle &&
				offSiteBannerPosition === 0 &&
				!showOffSite && (
					<ErrorMessage
						errorLabel={labels
							.get('CLEAR_MESSAGE_OFFSITE')
							.replace('{accountName}', dealershipName)}
					/>
				)}
			<ul
				className={setClassNames([
					isGridLayout ? 'vehicle-card-grid' : 'vehicle-card-list',
					'list-unstyled',
					'transition-property-opacity',
					'transition-duration-sm',
					'transition-timing-function-standard',
					isRequesting || isTransition ? 'opacity-25' : null
				])}
			>
				{cards}
			</ul>
		</React.Fragment>
	);
};

VehicleCards.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool.isRequired,
	isMyCarsSitePropEnabled: PropTypes.bool.isRequired,
	isRequesting: PropTypes.bool.isRequired,
	isTransition: PropTypes.bool.isRequired,
	inventoryObj: PropTypes.shape({
		incentives: PropTypes.shape({}),
		accounts: PropTypes.shape({}),
		pageInfo: PropTypes.shape({}),
		inventory: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	loadingDelayed: PropTypes.bool.isRequired
};

export default VehicleCards;
